import React, { useState } from "react";

import { resolveSignalCopy, templateFields } from "./signalCTAs";
import CommonModal from "../../Components/Modal";
import { useAppSelector } from "../../Store/Hooks";
import type {
  SignalCard,
  SignalTier,
} from "../../Store/Reducers/SignalFeedSlice";

/** Same pills as the card's own signal row (ActionCard PILL_STYLE). */
const PILL: Record<SignalTier, string> = {
  critical: "bg-[rgba(220,38,38,0.12)] text-[#dc2626]",
  warning: "bg-[rgba(217,119,6,0.12)] text-[#b45309]",
  opp: "bg-[rgba(13,148,136,0.12)] text-[#0d9488]",
  maint: "bg-[#f3f4f6] text-[#6b7280]",
};
const LABEL: Record<SignalTier, string> = {
  critical: "Critical",
  warning: "Warning",
  opp: "Opportunity",
  maint: "Maintenance",
};

interface SecondarySignalsProps {
  card: SignalCard;
  compact?: boolean;
}

/** One card per account: the account's other open signals behind
 * "+N more signals" — the v4 mock's `.card-secondary-signals`. They open in
 * a modal rather than inline, so the card keeps its size in the grid. They're
 * next in line: Mark as handled on the primary brings the first one up. */
const SecondarySignals: React.FC<SecondarySignalsProps> = ({
  card,
  compact,
}) => {
  const [open, setOpen] = useState(false);
  const parameters = useAppSelector(state => state.signalFeed.parameters);
  const others = card.secondary ?? [];
  if (!others.length) return null;
  const label = `+${others.length} more signal${others.length === 1 ? "" : "s"}`;
  return (
    <div
      className={
        compact
          ? "mb-[7px]"
          : // No border-b: the actions block's border-t is the divider. With
            // both, a lone card drew a doubled line and a card in a row drew
            // two lines with the row's leftover height between them.
            "bg-[#f9fafb] px-5 pb-2 pt-1.5"
      }
    >
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className="cursor-pointer border-0 bg-transparent p-0 text-[11px] font-bold tracking-[0.01em] text-brand hover:text-blue-600"
      >
        {label}
      </button>
      <CommonModal
        isOpen={open}
        toggle={() => setOpen(false)}
        usePortal
        size="lg"
        title={`More signals · ${card.account_name}`}
        subtitle={
          <span className="text-[12px] text-gray-500">
            Next in line — Mark as handled on the current signal brings the
            first one up.
          </span>
        }
      >
        <div className="flex flex-col">
          {others.map((s, i) => {
            const copy = resolveSignalCopy(
              s.signal_key,
              templateFields(s.evidence?.triggeredFields, parameters),
              s.cta?.card_copy
            );
            return (
              <div
                key={s.signal_firing_id}
                data-secondary-signal={s.signal_key}
                className={`flex items-start gap-[9px] py-3 ${
                  i ? "border-t border-[#e5e7eb]" : "pt-0"
                }`}
              >
                <span
                  className={`mt-0.5 shrink-0 rounded-[4px] px-1.5 py-px text-[9px] font-extrabold uppercase tracking-[0.07em] ${PILL[s.tier]}`}
                >
                  {LABEL[s.tier]}
                </span>
                <span className="pt-px text-[13px] leading-[1.45] text-[#4b5563]">
                  <strong className="text-gray-900">{copy.title}</strong>
                  {copy.text ? <> — {copy.text}</> : null}
                </span>
              </div>
            );
          })}
        </div>
      </CommonModal>
    </div>
  );
};

export default SecondarySignals;
